import { useEffect, useState, useRef, useCallback } from 'react';
import { getSocket } from '../lib/socket';

/**
 * Hook to watch a monitor run in real-time via Socket.IO
 * Uses the shared authenticated socket from the socket manager
 * @param {string} runId - The ID of the run to watch
 * @param {boolean} enabled - Whether to enable the connection
 * @returns {Object} - { runData, steps, isComplete, completionData, isConnected, isInitialized }
 */
export function useMonitorRunStream(runId, enabled = true) {
    const [runData, setRunData] = useState(null);
    const [steps, setSteps] = useState([]);
    const [isComplete, setIsComplete] = useState(false);
    const [completionData, setCompletionData] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const socketRef = useRef(null);

    useEffect(() => {
        if (!runId || !enabled) return;

        const socket = getSocket();
        
        // Socket not ready yet - will be created when auth initializes
        // This prevents race condition where this hook runs before AuthContext's initSocket
        if (!socket) {
            console.log('[useMonitorRunStream] Socket not ready yet, waiting for auth init');
            return;
        }
        
        socketRef.current = socket;

        // Handle connection state
        const handleConnect = () => {
            console.log('[useMonitorRunStream] Socket connected');
            setIsConnected(true);
            socket.emit('run:watch', runId);
        };

        const handleDisconnect = () => {
            console.log('[useMonitorRunStream] Socket disconnected');
            setIsConnected(false);
        };

        // Initial state when first connecting
        const handleInitial = (data) => {
            if (data.runId === runId) {
                console.log(`[useMonitorRunStream] Received initial state:`, data);
                setRunData(data);
                setSteps(data.steps || []);
                setIsInitialized(true);
                
                if (data.isComplete) {
                    setIsComplete(true);
                    setCompletionData(data);
                }
            }
        };

        // Incremental step updates
        const handleStep = (data) => {
            if (data.runId === runId) {
                console.log(`[useMonitorRunStream] Received step ${data.stepIndex}`);
                setSteps(prev => {
                    const newSteps = [...prev];
                    newSteps[data.stepIndex] = data.step;
                    return newSteps;
                });
            }
        };

        // Run completion
        const handleCompleted = (data) => {
            if (data.runId === runId) {
                console.log('[useMonitorRunStream] Run completed:', data);
                setIsComplete(true);
                setCompletionData(data);
                setRunData(prev => ({
                    ...prev,
                    success: data.success,
                    endTime: data.endTime,
                    duration: data.duration,
                    result: data.result,
                    note: data.note
                }));
            }
        };

        // Handle errors from server
        const handleError = (error) => {
            console.error('[useMonitorRunStream] Error:', error);
        };

        // Attach listeners
        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);
        socket.on('run:initial', handleInitial);
        socket.on('run:step', handleStep);
        socket.on('run:completed', handleCompleted);
        socket.on('error', handleError);

        // If already connected, start watching immediately
        if (socket.connected) {
            setIsConnected(true);
            socket.emit('run:watch', runId);
        }

        return () => {
            // Remove our listeners (don't disconnect shared socket)
            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
            socket.off('run:initial', handleInitial);
            socket.off('run:step', handleStep);
            socket.off('run:completed', handleCompleted);
            socket.off('error', handleError);
            
            // Unsubscribe from watching this run
            if (socket.connected) {
                socket.emit('run:unwatch', runId);
            }
        };
    }, [runId, enabled]);

    return {
        runData,
        steps,
        isComplete,
        completionData,
        isConnected,
        isInitialized,
        socket: socketRef.current
    };
}

/**
 * Hook to watch a monitor for new runs
 * Uses the shared authenticated socket from the socket manager
 * @param {string} monitorId - The ID of the monitor to watch
 * @param {boolean} enabled - Whether to enable the connection
 * @returns {Object} - { latestRunId, isConnected }
 */
export function useMonitorWatch(monitorId, enabled = true) {
    const [latestRunId, setLatestRunId] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const socketRef = useRef(null);

    useEffect(() => {
        if (!monitorId || !enabled) return;

        const socket = getSocket();
        
        // Socket not ready yet - will be created when auth initializes
        if (!socket) {
            console.log('[useMonitorWatch] Socket not ready yet, waiting for auth init');
            return;
        }
        
        socketRef.current = socket;

        const handleConnect = () => {
            console.log('[useMonitorWatch] Socket connected');
            setIsConnected(true);
            socket.emit('monitor:watch', monitorId);
            console.log(`[useMonitorWatch] Watching monitor ${monitorId}`);
        };

        const handleDisconnect = () => {
            console.log('[useMonitorWatch] Socket disconnected');
            setIsConnected(false);
        };

        // Listen for new completed runs
        const handleRunCompleted = (data) => {
            if (data.monitorId === monitorId) {
                console.log('[useMonitorWatch] Monitor run completed:', data);
                setLatestRunId(data.runId);
            }
        };

        // Handle errors from server
        const handleError = (error) => {
            console.error('[useMonitorWatch] Error:', error);
        };

        // Attach listeners
        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);
        socket.on('monitor:runCompleted', handleRunCompleted);
        socket.on('error', handleError);

        // If already connected, start watching immediately
        if (socket.connected) {
            setIsConnected(true);
            socket.emit('monitor:watch', monitorId);
            console.log(`[useMonitorWatch] Watching monitor ${monitorId}`);
        }

        return () => {
            // Remove our listeners (don't disconnect shared socket)
            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
            socket.off('monitor:runCompleted', handleRunCompleted);
            socket.off('error', handleError);
        };
    }, [monitorId, enabled]);

    return {
        latestRunId,
        isConnected,
        socket: socketRef.current
    };
}
